import useAccount from 'hooks/account/useAccount/useAccount'
import * as React from 'react'
import Button from 'theme/atoms/Button'
import styled from 'styled-components'
import theme from 'theme'

import { ReactComponent as CloseIcon } from 'assets/close.svg'
import useTranslations from 'hooks/useTranslations'
import { useDisplayVariant } from 'modules/productDetail'
import * as events from '../../events'

type Props = {
  channelInactive: 'b2b' | 'b2c' | null
}

export default function ChannelPopUp(props: Props) {
  const account = useAccount()
  const variant = useDisplayVariant()
  const t = useTranslations<'templates-PDP'>()
  const newChannel = account.channel === 'b2b' ? 'b2c' : 'b2b'
  const [showPopup, setShowPopup] = React.useState(true)

  // https://jira.em-group.de/browse/WDV-2380
  React.useEffect(() => {
    if (variant.data.isDummy) return
    events.showChannelRestrictionPopup(variant.data.sku, variant.data.title)
  }, [variant.data.sku])

  /** Hide popup if
   * - user account is currently fetching (to prevent initial flicker effect) or
   * - user is logged in or
   * - current channel is allowed to buy or
   * - user closes the Popup */
  if (
    account.isFetching ||
    account.loggedIn ||
    !props.channelInactive ||
    !showPopup
  )
    return null

  return (
    <Wrapper className="ChannelPopUp" data-cy-state={'show-channel-popup'}>
      <div className="headline-container">
        <h3>{t(`only_for_${props.channelInactive}_available`)}</h3>
        <div
          data-cy-handle="close-channel-popup"
          onClick={() => setShowPopup(false)}
        >
          <CloseIcon />
        </div>
      </div>
      <div className="action-button">
        <div className="description">{t('change_customer_group')}</div>
        <div className="status">
          {t('channel_status', {
            STATUS: '_',
            customer: t(props.channelInactive === 'b2b' ? 'b2c' : 'b2b'),
            __transformers: {
              STATUS: (_, vars) => <strong>{vars.STATUS}</strong>
            }
          })}
        </div>
        <Button
          $fullWidth
          variation="secondary"
          data-cy-handle="switch-channel-popup-button"
          onClick={() => {
            account.setChannel(
              newChannel,
              newChannel === 'b2c'
                ? `overlay_b2c_pdp_click`
                : `overlay_b2b_pdp_click`
            )
            events.continueAsB2bClick(variant.data.sku, variant.data.title)
          }}
        >
          {t(`go_on_as_${props.channelInactive}`)}
        </Button>
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 100%;
  border: 1px solid ${theme.colors.primary};
  position: absolute;
  background: ${theme.colors.shade.primaryUltraBright};

  z-index: ${theme.zIndex.theme_templates_pdp_buybox_buywrapper_channelpopup};
  color: ${theme.colors.b0};
  bottom: 55px;
  padding: ${theme.spacing('m')};

  > .headline-container {
    display: flex;
    justify-content: space-between;
    ${theme.ty('rc-base', '700', 'rc-2xl')};

    > div {
      cursor: pointer;
      height: fit-content;
      > svg {
        width: 24px;
        align-self: baseline;
      }
    }
  }
  > .action-button {
    display: block;
    justify-content: center;
    margin-top: 0.625rem;
    > .description {
      ${theme.ty('rc-s', '400', 'rc-base')};
    }
    > .status {
      ${theme.ty('rc-s', '400', 'rc-base')};
      margin: 0.625rem 0 20px 0;
    }
  }

  :after,
  :before {
    bottom: -32px;
    left: 45%;
    border: solid transparent;
    content: '';
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
    @media (min-width: ${theme.ms.MD}px) {
      bottom: -32px;
    }
  }
  :after {
    border-color: rgba(0, 0, 0, 0);
    border-bottom-color: rgba(0, 0, 0, 0);
    border-top-color: ${theme.colors.shade.primaryUltraBright};
    border-left-color: rgba(0, 0, 0, 0);
    left: 45%;
    bottom: -29px;
    border-width: 15px;
    margin-left: 1px;
  }
  :before {
    border-color: rgba(0, 0, 0, 0);
    margin-left: -1rem;
    border-top-color: ${theme.colors.primary};
    border-left-color: rgba(0, 0, 0, 0);
    margin-left: auto;
    border-width: 16px;
    @media (min-width: ${theme.ms.MD}px) {
      margin-top: -3.25rem;
    }
  }
`
